import { create } from 'zustand';

export type PluginId = 'measurement' | 'annotation' | 'profile' | 'viewshed' | 'export' | 'terrain-analysis' | null;

export type EngineType = 'maplibre' | 'cesium' | 'deck' | 'three' | null;

export type PluginState = 'idle' | 'active' | 'computing' | 'error' | 'backend-not-ready';

interface PluginStoreState {
  /** Currently active plugin */
  activePluginId: PluginId;
  /** Current engine state - determines plugin availability */
  engineType: EngineType;
  /** Engine ready state - affects plugin functionality */
  engineReady: boolean;
  /** Individual plugin states */
  pluginStates: Record<string, PluginState>;
  /** Transient plugin data for active sessions */
  transient: {
    measurement: {
      vertices: Array<[number, number]>;
      kind: 'linear' | 'area' | 'volume' | 'clearance' | 'angle';
      result?: { value: number; unit: string };
    };
    viewshed: {
      observerPoint?: [number, number];
      height: number;
      maxDistance: number;
      fov: number;
    };
    profile: {
      routeId?: string;
    };
    annotation: {
      mode: 'point' | 'line' | 'polygon' | 'rectangle' | 'circle' | 'select';
    };
  };

  /** Actions */
  setActivePlugin: (pluginId: PluginId) => void;
  setEngineType: (engineType: EngineType) => void;
  setEngineReady: (ready: boolean) => void;
  setPluginState: (pluginId: string, state: PluginState) => void;
  updateMeasurementTransient: (updates: Partial<PluginStoreState['transient']['measurement']>) => void;
  updateViewshedTransient: (updates: Partial<PluginStoreState['transient']['viewshed']>) => void;
  updateProfileTransient: (updates: Partial<PluginStoreState['transient']['profile']>) => void;
  updateAnnotationTransient: (updates: Partial<PluginStoreState['transient']['annotation']>) => void;
  resetTransient: (pluginId: PluginId) => void;
  /** Check if a plugin is available for the current engine */
  isPluginAvailable: (pluginId: PluginId) => boolean;
  /** Get plugin state with engine readiness consideration */
  getPluginState: (pluginId: PluginId) => PluginState;
}

const initialTransientState = {
  measurement: {
    vertices: [],
    kind: 'linear' as const,
  },
  viewshed: {
    height: 1.8,
    maxDistance: 1000,
    fov: 90,
  },
  profile: {},
  annotation: {
    mode: 'point' as const,
  },
};

export const usePluginStore = create<PluginStoreState>((set, get) => ({
  activePluginId: null,
  engineType: null,
  engineReady: false,
  pluginStates: {},
  transient: initialTransientState,

  setActivePlugin: (activePluginId) => set({ activePluginId }),

  setEngineType: (engineType) => set({ engineType }),

  setEngineReady: (engineReady) => 
    set((state) => {
      // If engine becomes not ready, deactivate plugins that require engine support
      if (!engineReady && state.activePluginId && ['measurement', 'viewshed', 'profile'].includes(state.activePluginId)) {
        return { engineReady, activePluginId: null };
      }
      return { engineReady };
    }),

  setPluginState: (pluginId, pluginState) =>
    set((state) => ({
      pluginStates: { ...state.pluginStates, [pluginId]: pluginState },
    })),

  updateMeasurementTransient: (updates) =>
    set((state) => ({
      transient: {
        ...state.transient,
        measurement: { ...state.transient.measurement, ...updates },
      },
    })),

  updateViewshedTransient: (updates) =>
    set((state) => ({
      transient: {
        ...state.transient,
        viewshed: { ...state.transient.viewshed, ...updates },
      },
    })),

  updateProfileTransient: (updates) =>
    set((state) => ({
      transient: {
        ...state.transient,
        profile: { ...state.transient.profile, ...updates },
      },
    })),

  updateAnnotationTransient: (updates) =>
    set((state) => ({
      transient: {
        ...state.transient,
        annotation: { ...state.transient.annotation, ...updates },
      },
    })),

  resetTransient: (pluginId) =>
    set((state) => {
      if (!pluginId) return state;
      const key = pluginId as keyof typeof initialTransientState;
      return {
        transient: {
          ...state.transient,
          [key]: { ...initialTransientState[key] },
        },
      };
    }),

  isPluginAvailable: (pluginId) => {
    const { engineType, engineReady } = get();
    
    if (!pluginId) return false;
    
    // Export and annotation plugins work without engine
    if (['export', 'annotation'].includes(pluginId)) return true;
    
    // Other plugins require engine support
    if (!engineType || !engineReady) return false;
    
    // Plugin-specific engine compatibility
    switch (pluginId) {
      case 'measurement':
        return ['maplibre', 'cesium', 'deck'].includes(engineType);
      case 'profile':
        return ['maplibre', 'cesium'].includes(engineType);
      case 'viewshed':
        return ['maplibre', 'cesium'].includes(engineType);
      default:
        return false;
    }
  },

  getPluginState: (pluginId) => {
    const state = get();
    
    if (!pluginId) return 'idle';
    
    // Check if plugin is available first
    if (!state.isPluginAvailable(pluginId)) {
      return 'backend-not-ready';
    }
    
    // Return specific plugin state or default to idle
    return state.pluginStates[pluginId] ?? 'idle';
  },
}));